import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import ReactMarkdown from 'react-markdown';
import { apiService } from '../services/api';
import { FinancialData, Company } from '../types';
import FinancialCharts from '../components/FinancialCharts';

const CompanyDetail: React.FC = () => {
  const { securityCode } = useParams<{ securityCode: string }>();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState<any>(null);
  const [analysisLoading, setAnalysisLoading] = useState<boolean>(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>('');

  // 証券コードから企業の財務データを直接取得
  const { data: initialData, error: financialDataError } = useSWR<FinancialData>(
    securityCode ? ['company-data', securityCode] : null,
    async () => {
      if (!securityCode) throw new Error('証券コードが指定されていません');
      console.log('Fetching company data for:', securityCode);
      const result = await apiService.getCompanyDataBySecurityCode(securityCode);
      console.log('Company data result:', result);
      return result;
    }
  );

  const companyName = initialData?.company_name;

  // 利用可能な年度から選択年度を決定
  const availableYears = initialData?.available_years || [];
  const currentSelectedYear = selectedYear || (availableYears.length > 0 ? availableYears[0] : '');

  // 選択年度のデータを取得
  const { data: companyData, error, isLoading } = useSWR<FinancialData>(
    companyName && currentSelectedYear ? ['company', companyName, currentSelectedYear] : null,
    async () => {
      if (!companyName) throw new Error('企業名が指定されていません');
      return await apiService.getCompanyData(decodeURIComponent(companyName), currentSelectedYear);
    }
  );

  // 証券コードがない場合のみリダイレクト（companyNameは非同期取得のため除外）
  useEffect(() => {
    if (!securityCode) {
      navigate('/');
    }
  }, [securityCode, navigate]);

  // Gemini分析を取得する関数
  const fetchAnalysis = async () => {
    if (!companyName || !currentSelectedYear) return;
    
    setAnalysisLoading(true);
    setAnalysisError(null);
    
    try {
      const analysisData = await apiService.getCompanyAnalysis(decodeURIComponent(companyName), currentSelectedYear);
      setAnalysis(analysisData);
    } catch (error: any) {
      setAnalysisError(error.message || 'AI分析の取得に失敗しました');
    } finally {
      setAnalysisLoading(false);
    }
  };

  // 年度が変更された時または企業データが変更された時に分析を取得
  useEffect(() => {
    // 分析をリセット
    setAnalysis(null);
    setAnalysisError(null);
    setAnalysisLoading(false);
    
    if (companyData && companyName && currentSelectedYear) {
      // 既にDynamoDBにanalyticsがある場合はそれを使用
      if (companyData.analytics) {
        setAnalysis({
          company_name: companyName,
          analysis: companyData.analytics,
          cached: true,
          year: currentSelectedYear
        });
      } else {
        // analyticsがない場合のみAI分析を実行
        fetchAnalysis();
      }
    }
  }, [companyData, currentSelectedYear]);

  // ローディング中または証券コードがない場合は先にローディング表示
  if (isLoading || !securityCode) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        データを読み込んでいます...
      </div>
    );
  }

  // 企業データが見つからない場合
  if (financialDataError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ 
          backgroundColor: '#fed7d7', 
          color: '#c53030', 
          padding: '1rem', 
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          ⚠️ 証券コード「{securityCode}」の企業が見つかりませんでした
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '1rem',
            backgroundColor: '#3182ce',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          検索画面に戻る
        </button>
      </div>
    );
  }

  // 企業データが取得できていない場合はローディング
  if (!initialData || !companyName) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        企業データを取得しています...
      </div>
    );
  }

  if (!companyData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ color: '#666', marginBottom: '1rem' }}>
          企業データが見つかりません
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          トップページに戻る
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
      {/* ナビゲーションバー */}
      <div style={{
        backgroundColor: 'white',
        padding: '1rem',
        marginBottom: '2rem',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#e2e8f0',
            color: '#4a5568',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          ← 企業検索画面に戻る
        </button>
        
        <div style={{ fontSize: '1.1rem', color: '#2d3748', fontWeight: '500' }}>
          {companyData.company_name}
        </div>
        
        {/* 年度選択 */}
        {availableYears.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem', color: '#666' }}>決算年度:</span>
            <select
              value={currentSelectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              style={{
                padding: '0.5rem',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '0.9rem',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}年度</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* 財務チャートコンポーネント */}
      <FinancialCharts companyData={companyData} />

      {/* AI分析セクション */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        marginTop: '2rem',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ 
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ 
            color: '#2d3748', 
            fontSize: '1.5rem', 
            margin: 0,
            borderBottom: '2px solid #e2e8f0',
            paddingBottom: '0.5rem'
          }}>
            🤖 AI企業分析（Gemini） {currentSelectedYear && `- ${currentSelectedYear}年度`}
          </h2>
        </div>

        {analysisLoading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ 
              display: 'inline-block',
              width: '20px',
              height: '20px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '1rem'
            }}></div>
            <p style={{ color: '#666', fontSize: '1.1rem' }}>
              AI分析を実行しています...（30-60秒程度お待ちください）
            </p>
            <style dangerouslySetInnerHTML={{
              __html: `
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `
            }} />
          </div>
        )}

        {analysisError && (
          <div style={{
            backgroundColor: '#fed7d7',
            color: '#c53030',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            ⚠️ {analysisError}
          </div>
        )}

        {analysis && !analysisLoading && (
          <div style={{ 
            backgroundColor: '#f8f9fa',
            padding: '1.5rem',
            borderRadius: '8px',
            lineHeight: '1.8'
          }}>
            <div style={{ 
              color: '#2d3748',
              fontSize: '1rem'
            }}>
              <ReactMarkdown
                components={{
                  h2: ({node, ...props}) => <h3 style={{color: '#2d3748', fontSize: '1.3rem', marginTop: '1.5rem', marginBottom: '0.8rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.3rem'}} {...props} />,
                  h3: ({node, ...props}) => <h4 style={{color: '#4a5568', fontSize: '1.1rem', marginTop: '1.2rem', marginBottom: '0.6rem', fontWeight: '600'}} {...props} />,
                  p: ({node, ...props}) => <p style={{marginBottom: '0.8rem', lineHeight: '1.6'}} {...props} />,
                  ul: ({node, ...props}) => <ul style={{marginLeft: '1.2rem', marginBottom: '0.8rem'}} {...props} />,
                  li: ({node, ...props}) => <li style={{marginBottom: '0.3rem'}} {...props} />,
                  strong: ({node, ...props}) => <strong style={{fontWeight: '600', color: '#2d3748'}} {...props} />
                }}
              >
                {analysis.analysis}
              </ReactMarkdown>
            </div>
            {analysis.generated_at && (
              <div style={{ 
                marginTop: '2rem',
                fontSize: '0.85rem',
                color: '#666',
                textAlign: 'right',
                borderTop: '1px solid #e2e8f0',
                paddingTop: '1rem'
              }}>
                分析生成日時: {new Date(analysis.generated_at).toLocaleString('ja-JP')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDetail;
